import type { AuditEntry } from '@/bindings/AuditEntry'
import { Badge } from '@/components/badge'
import { Card } from '@/components/layout'
import { renderAuditEntry } from '@/features/audit-log/render'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { ROLE_LABELS, isRole } from '@/lib/roles'
import { formatDateTime } from '@/lib/time'

/** One audit row: when, who, what, and the before/after table when there is one. */
export function AuditEntryRow({ entry }: { entry: AuditEntry }) {
  const t = useT()
  const language = useLanguage()
  const { text, changes } = renderAuditEntry(entry, t, language)
  const { actor } = entry
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <time dateTime={entry.created_at} className="text-muted">
          {formatDateTime(entry.created_at, language)}
        </time>
        {actor.kind === 'customer' ? (
          <Badge tone="muted">{t('audit.actor.customer')}</Badge>
        ) : (
          <>
            <span className="font-medium">{actor.name ?? t('audit.actor.unknown')}</span>
            {actor.kind === 'platform_user' && <Badge tone="warning">{t('audit.actor.platform')}</Badge>}
            {actor.kind === 'platform_user' && actor.role === 'admin' && <Badge tone="muted">{t('audit.actor.admin')}</Badge>}
            {actor.kind === 'restaurant_user' && isRole(actor.role) && <Badge tone="muted">{t(ROLE_LABELS[actor.role])}</Badge>}
          </>
        )}
      </div>
      <p className="wrap-break-word">{text}</p>
      {changes.length > 0 && (
        <table aria-label={t('audit.changes')} className="w-full text-sm">
          <thead className="text-muted text-left text-xs">
            <tr>
              <th scope="col" className="pr-3 font-medium">
                {t('audit.col.field')}
              </th>
              <th scope="col" className="pr-3 font-medium">
                {t('audit.col.from')}
              </th>
              <th scope="col" className="font-medium">
                {t('audit.col.to')}
              </th>
            </tr>
          </thead>
          <tbody>
            {changes.map((change) => (
              <tr key={change.field}>
                <th scope="row" className="pr-3 text-left font-medium">
                  {change.label}
                </th>
                <td className="text-muted pr-3 break-all">{change.from}</td>
                <td className="break-all">{change.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}
